<template>
    <button
        class="tw-rounded-lg tw-text-left tw-border tw-bg-card tw-text-card-foreground tw-shadow-2xs tw-overflow-hidden"
        @click="editInvoice"
    >
        <div class="tw-p-5 tw-pb-3">
            <div class="tw-flex tw-items-start tw-justify-between tw-mb-1">
                <div class="tw-flex tw-items-top tw-gap-3">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-file-text tw-h-16 tw-w-16 tw-text-gray-400"
                    >
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                        <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                        <path d="M10 9H8"></path>
                        <path d="M16 13H8"></path>
                        <path d="M16 17H8"></path>
                    </svg>
                    <div>
                        <h4 class="tw-font-sm tw-text-gray-900">{{ doc.file?.file_name }}</h4>
                        <p class="tw-text-sm tw-text-gray-500">
                            {{ fileSize(doc.file?.file_size) ?? 'Unknown' }} • Uploaded by
                            {{ doc.user?.name ?? 'Unknown' }} {{ friendlyDate(doc.created_at) }}
                        </p>
                    </div>
                </div>
                <div v-if="progress.overall === 2" class="tw-flex tw-items-center tw-gap-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="lucide lucide-circle-check-big tw-h-4 tw-w-4 tw-text-green-500"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <div
                        class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-green-100 tw-text-green-800"
                    >
                        Completed
                    </div>
                </div>
                <div v-else-if="progress.overall === 1" class="tw-flex tw-items-center tw-gap-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="lucide lucide-loader tw-h-4 tw-w-4 tw-animate-spin tw-text-blue-500"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M12 2a10 10 0 1 0 10 10"></path>
                    </svg>
                    <div
                        class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-blue-100 tw-text-blue-800"
                    >
                        Processing
                    </div>
                </div>
                <div v-else-if="progress.overall === 0" class="tw-flex tw-items-center tw-gap-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="lucide lucide-clock tw-h-4 tw-w-4 tw-text-gray-500"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                    </svg>
                    <div
                        class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-gray-100 tw-text-gray-800"
                    >
                        Queued
                    </div>
                </div>
                <div v-else-if="progress.overall === -1" class="tw-flex tw-items-center tw-gap-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="lucide lucide-circle-x tw-h-4 tw-w-4 tw-text-red-500"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m15 9-6 6"></path>
                        <path d="m9 9 6 6"></path>
                    </svg>
                    <div
                        class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-red-100 tw-text-red-800"
                    >
                        Failed
                    </div>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                <div
                    class="tw-flex tw-items-center tw-gap-1"
                    :class="{
                        'tw-text-red-600': progress.upload === -1,
                        'tw-text-gray-400': progress.upload === 0,
                        'tw-text-orange-600': progress.upload === 1,
                        'tw-text-green-600': progress.upload === 2,
                    }"
                >
                    <i class="mdi mdi-upload"></i>
                    Upload
                </div>
                <i class="mdi mdi-chevron-right tw-text-xl tw-m-0"></i>
                <div
                    class="tw-flex tw-items-center tw-gap-1"
                    :class="{
                        'tw-text-red-600': progress.ocr === -1,
                        'tw-text-gray-400': progress.ocr === 0,
                        'tw-text-orange-600': progress.ocr === 1,
                        'tw-text-green-600': progress.ocr === 2,
                    }"
                >
                    <i class="mdi mdi-magnify-scan"></i>
                    OCR Scan
                </div>
                <i class="mdi mdi-chevron-right tw-text-xl tw-m-0"></i>
                <div
                    class="tw-flex tw-items-center tw-gap-1"
                    :class="{
                        'tw-text-red-600': progress.ai_analysis === -1,
                        'tw-text-gray-400': progress.ai_analysis === 0,
                        'tw-text-orange-600': progress.ai_analysis === 1,
                        'tw-text-green-600': progress.ai_analysis === 2,
                    }"
                >
                    <i class="mdi mdi-robot"></i>
                    AI Analysis
                </div>
                <i class="mdi mdi-chevron-right tw-text-xl tw-m-0"></i>
                <div
                    class="tw-flex tw-items-center tw-gap-1"
                    :class="{
                        'tw-text-red-600': progress.invoice === -1,
                        'tw-text-gray-400': progress.invoice === 0,
                        'tw-text-orange-600': progress.invoice === 1,
                        'tw-text-green-600': progress.invoice === 2,
                    }"
                >
                    <i class="mdi mdi-invoice-list"></i>
                    Invoice Creation
                </div>
            </div>
        </div>
        <div
            v-if="progress.message"
            class="tw-text-sm tw-text-white tw-p-3 tw-border-t tw-border-gray-200"
            :class="{
                'tw-bg-red-600': progress.overall === -1,
                'tw-bg-gray-400': progress.overall === 0,
                'tw-bg-orange-600': progress.overall === 1,
                'tw-bg-green-600': progress.overall === 2,
            }"
        >
            {{ progress.message }}
        </div>
    </button>
</template>

<script setup>
import { computed } from 'vue';
import { DateTime } from 'luxon';
import { filesize } from 'filesize';

const props = defineProps({
    doc: {
        type: Object,
        required: true,
    },
});

const fileSize = (size) => {
    if (size == null) {
        return null;
    }

    return filesize(size, { standard: 'jedec' });
};

const friendlyDate = (date) => {
    if (!date) {
        return 'just now';
    }

    const parsed = DateTime.fromISO(date);

    if (!parsed.isValid) {
        return 'just now';
    }

    return parsed.toRelative({ base: DateTime.now() }) ?? 'just now';
};

const baseProgress = Object.freeze({
    upload: 2,
    ocr: 0,
    ai_analysis: 0,
    invoice: 0,
    overall: 0,
    message: '',
});

const progress = computed(() => ({
    ...baseProgress,
    ...(props.doc.status_progress ?? {}),
}));

const editInvoice = () => {
    if (
        (progress.value.overall === -1 && (progress.value.ai_analysis === -1 || progress.value.invoice === -1)) ||
        progress.value.overall === 2
    ) {
        Eventbus.$emit('editOCRInvoice', props.doc.id);
    }
};
</script>
